import { useMemo, useState } from "react";
import { DatasetListResponse } from "datasets/types/interfaces/datasetListItem";
import { Play } from "lucide-react";
import { Button } from "~/components/ui/button";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useFetcher } from "~/hooks/useFetcher";
import { AdminPage } from "~/pages/manifold-nextgen/components/admin/AdminPage";
import { catalogDatasetsListUrl } from "~/services/catalog";
import { CaseDetailSheet } from "./CaseDetailSheet";
import { EvalsOverview } from "./EvalsOverview";
import { LaunchRunDialog } from "./LaunchRunDialog";
import { RunDetailSheet } from "./RunDetailSheet";
import { EVALS_PAGE_TITLE, MATRIX_RUN_COLUMNS } from "./constants/page";
import type { DatasetRefs } from "./primitives/chips";
import { TruncationNotice } from "./primitives/notices";
import { buildBrokenAndFlakyCases, buildCaseHistory } from "./selectors/cases";
import { collectTags } from "./selectors/facets";
import { buildKpis } from "./selectors/kpis";
import { buildRunTimeTrend, buildTrend } from "./selectors/runs";
import {
  sortRunsChronologically,
  useEvalCaseHistory,
  useEvalCases,
  useEvalRuns,
  useResultsForRuns,
  useRunAgentLabels,
} from "./useEvalApi";

/** Rows in the broken-and-flaky and recent-runs cards: a glance, not the table. */
const OVERVIEW_ROW_COUNT = 3;

/**
 * The evals landing page: headline figures, the score and run-time trends, and the
 * few cases and runs most worth opening. Every figure is computed over the same
 * window of recent runs; the full tables live on the runs and cases pages.
 */
const EvalsPage = () => {
  useDocumentTitle(EVALS_PAGE_TITLE);
  // No header breadcrumbs, matching every other nextgen list root: the nav
  // picker and the Admin sidebar's active entry already say where this is, and
  // a lone crumb repeating the page's own heading is not a trail.

  const [launchOpen, setLaunchOpen] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  // Set when a run's cell is clicked rather than the row, so the drawer opens on that run.
  const [selectedCaseRunId, setSelectedCaseRunId] = useState<string | null>(null);

  // Memoised: the header compares its actions deeply, and an inline closure is a
  // new value every render, which would set header state on each one.
  const headerActions = useMemo<[React.ReactNode]>(
    () => [
      <Button key="launch-run" size="sm" onClick={() => setLaunchOpen(true)}>
        <Play className="size-4" aria-hidden />
        Launch run
      </Button>,
    ],
    []
  );
  useNextGenHeader({ actions: headerActions });

  const { data: casesPage, isLoading: casesLoading, mutate: refetchCases } = useEvalCases();
  const { data: runsPage, isLoading: runsLoading, mutate: refetchRuns } = useEvalRuns();

  const allCases = casesPage?.items ?? [];
  const allRuns = runsPage?.items ?? [];

  // Every figure here is computed over these two lists, so a list that stopped
  // short does not shorten a table, it changes the numbers. Stated rather than
  // left to be inferred, because nothing else on the page would look wrong.
  const truncations = [
    casesPage?.truncated
      ? { noun: "cases", shown: allCases.length, total: casesPage.totalCount }
      : null,
    runsPage?.truncated
      ? { noun: "runs", shown: allRuns.length, total: runsPage.totalCount }
      : null,
  ].filter((entry): entry is { noun: string; shown: number; total: number } => entry != null);

  const windowRuns = sortRunsChronologically(allRuns).slice(-MATRIX_RUN_COLUMNS);
  // The list arrives newest first, which is the order the card shows.
  const recentRuns = allRuns.slice(0, OVERVIEW_ROW_COUNT);
  const agentLabels = useRunAgentLabels(recentRuns.map((run) => run.agent_id));

  const { data: resultsBundle, isLoading: resultsLoading } = useResultsForRuns(
    windowRuns.map((run) => run.id)
  );
  const resultsByRun = resultsBundle?.resultsByRun ?? {};

  // Each case against its own newest runs, so "broken" and "flaky" are judged over
  // that case's history rather than over a window it may never have been in.
  const { data: history, isLoading: historyLoading } = useEvalCaseHistory({
    tags: [],
    agentIds: [],
    runsPerCase: MATRIX_RUN_COLUMNS,
  });
  const suiteHistory = buildCaseHistory(allCases, history?.results ?? [], history?.runs ?? []);

  const { data: catalogDatasetList } = useFetcher<DatasetListResponse>(catalogDatasetsListUrl);
  const datasets: DatasetRefs = Object.fromEntries(
    (catalogDatasetList?.items ?? []).map((dataset) => [
      dataset.id,
      { title: dataset.title, accent_color: dataset.accent_color },
    ])
  );

  const isLoading = casesLoading || runsLoading || resultsLoading || historyLoading;

  return (
    <>
      <AdminPage
        title={EVALS_PAGE_TITLE}
        /* The two nouns the cards and pages are named after; nothing else
           on the page defines them. */
        description="A case tests one query; a run is an evaluation of one or more cases."
        width="wide"
      >
        <div className="flex flex-col gap-4">
          <TruncationNotice truncations={truncations} />

          <EvalsOverview
            kpis={buildKpis(allCases, windowRuns, resultsByRun, suiteHistory)}
            scoreTrend={buildTrend(windowRuns, resultsByRun)}
            runTimeTrend={buildRunTimeTrend(windowRuns)}
            brokenAndFlaky={buildBrokenAndFlakyCases(suiteHistory, OVERVIEW_ROW_COUNT)}
            recentRuns={recentRuns}
            agentLabels={agentLabels}
            isLoading={isLoading}
            onSelectRun={setSelectedRunId}
            onSelectCase={(caseId, runId) => {
              setSelectedCaseId(caseId);
              setSelectedCaseRunId(runId ?? null);
            }}
          />
        </div>
      </AdminPage>

      {/* Mounted only while open, so the form resets between launches. */}
      {launchOpen && (
        <LaunchRunDialog
          onOpenChange={setLaunchOpen}
          cases={allCases}
          initialTags={[]}
          onLaunched={() => void refetchRuns()}
        />
      )}

      <RunDetailSheet
        open={selectedRunId != null}
        onOpenChange={(open) => !open && setSelectedRunId(null)}
        runId={selectedRunId}
        onDeleted={() => void refetchRuns()}
      />

      <CaseDetailSheet
        open={selectedCaseId != null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedCaseId(null);
            setSelectedCaseRunId(null);
          }
        }}
        caseId={selectedCaseId}
        initialRunId={selectedCaseRunId}
        history={suiteHistory.find((row) => row.evalCase.id === selectedCaseId) ?? null}
        datasets={datasets}
        availableTags={collectTags(allCases, [])}
        onSaved={() => void refetchCases()}
      />
    </>
  );
};

export default EvalsPage;
